import React, { useState, useEffect } from 'react';
import { 
  BookOpen, Plus, Edit2, Trash2, Search, X, Loader2
} from 'lucide-react';
import { subjectsAPI } from '../services/api';
import Pagination from '../components/Pagination';

const SubjectsPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchSubjects();
  }, [currentPage]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage };
      if (searchTerm) params.search = searchTerm;
      const response = await subjectsAPI.getAll(params);
      const data = response.data;
      if (data.results) {
        setSubjects(data.results);
        setTotalCount(data.count || 0);
      } else {
        setSubjects(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Fanlarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSubject) {
        await subjectsAPI.update(editingSubject.id, formData);
      } else {
        await subjectsAPI.create(formData);
      }
      fetchSubjects();
      closeModal();
    } catch (error) {
      console.error('Saqlashda xato:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Fanni o'chirishni tasdiqlaysizmi?")) {
      try {
        await subjectsAPI.delete(id);
        fetchSubjects();
      } catch (error) {
        console.error("O'chirishda xato:", error);
      }
    }
  };

  const openModal = (subject = null) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({ name: subject.name, description: subject.description || '' });
    } else {
      setEditingSubject(null);
      setFormData({ name: '', description: '' });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSubject(null);
    setFormData({ name: '', description: '' });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSubjects();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><BookOpen size={28} /> Fanlar</h1>
          <p>Barcha fanlar ro'yxati</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <Plus size={20} /> Yangi fan
        </button>
      </header>

      {/* Search */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Fanni qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn btn-sm btn-primary" onClick={handleSearch}>
            Qidirish
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <span>Jami: <strong>{totalCount}</strong> ta fan</span>
        {totalPages > 1 && <span>Sahifa: <strong>{currentPage}</strong> / {totalPages}</span>}
      </div>

      {/* Subjects Grid */}
      <div className="cards-grid">
        {subjects.map(subject => (
          <div key={subject.id} className="subject-card">
            <div className="subject-card-header">
              <BookOpen size={24} />
              <h3>{subject.name}</h3>
            </div>
            {subject.description && (
              <p className="subject-description">{subject.description}</p>
            )}
            <div className="subject-stats">
              <span>{subject.teachers_count || 0} o'qituvchi</span>
              <span>{subject.groups_count || 0} guruh</span>
            </div>
            <div className="subject-actions">
              <button className="btn btn-sm btn-outline" onClick={() => openModal(subject)}>
                <Edit2 size={16} /> Tahrirlash
              </button>
              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(subject.id)}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {subjects.length === 0 && (
        <div className="empty-state">
          <BookOpen size={48} />
          <p>Fanlar topilmadi</p>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <Plus size={20} /> Yangi fan qo'shish
          </button>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingSubject ? "Fanni tahrirlash" : "Yangi fan"}</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Fan nomi *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Masalan: Anatomiya"
                  required
                />
              </div>
              <div className="form-group">
                <label>Tavsif</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Fan haqida qisqacha ma'lumot..."
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingSubject ? "Saqlash" : "Yaratish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubjectsPage;
